const { Core, Utils, Events, Cache } = Global;
const { $fetch, $loading } = Utils;

/* -------------------------------------------
   Early hide + manual scroll restoration
   (Best possible inside this file)
-------------------------------------------- */
(function earlyHideOnBackForward() {
  try {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const nav = performance.getEntriesByType?.('navigation')?.[0];
    const type = nav?.type || 'navigate';

    const ref = document.referrer || '';
    const likelyBackFromProduct = ref.includes('/products/');

    if (type === 'back_forward' || likelyBackFromProduct) {
      // mark restore not done yet
      window.__col_restore_done = false;

      // inject style once
      if (!document.getElementById('col-restore-style')) {
        const style = document.createElement('style');
        style.id = 'col-restore-style';
        style.textContent = `html.__col_restore_hide body{opacity:0!important}`;
        document.head.appendChild(style);
      }

      // hide ASAP
      document.documentElement.classList.add('__col_restore_hide');

      // ✅ SAFE failsafe: only unhide if restore never finishes
      setTimeout(() => {
        if (!window.__col_restore_done) {
          document.documentElement.classList.remove('__col_restore_hide');
        }
      }, 4000);
    }
  } catch (e) {}
})();

function snapshotKey() {
  const u = new URL(window.location.href);
  u.searchParams.delete('page');
  u.searchParams.delete('section_id');
  return `COLLECTION_SNAPSHOT:${u.pathname}?${u.searchParams.toString()}`;
}

customElements.define('collection-provider', class extends Core {
  elements = { $: [['collection-form'], 'loader'] };

  propTypes = {
    'collection-url': String,
    'sort-by': String
  };

  _isRestoring = false;
  _restoreApplied = false;
  _scrollSaveT = null;
  _lastSrc = null;

  render() {
    this._tryRestoreSnapshot(); // restore as early as possible

    this._getForms();
    this.sub(Events.COLLECTION_CHANGE, this._handleChange);
    this._initCache();
    this.sortBy && this.sub(Events.COLLECTION_SORT_CHANGE, this._handleSortChange);

    document.addEventListener('click', this._onDocClickCapture, true);
    window.addEventListener('pageshow', this._onPageShow);
    window.addEventListener('scroll', this._onScroll, { passive: true });

    // ✅ IMPORTANT: DO NOT auto-unhide here.
    // Unhide should only happen:
    //  - after restore completes, or
    //  - in the safe failsafe timer above if restore never happens.
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClickCapture, true);
    window.removeEventListener('pageshow', this._onPageShow);
    window.removeEventListener('scroll', this._onScroll);
  }

  // -----------------------------
  // Snapshot restore/save
  // -----------------------------

  _onPageShow = () => {
    this._tryRestoreSnapshot();
  };

  _tryRestoreSnapshot() {
    if (this._restoreApplied) return;

    const raw = sessionStorage.getItem(snapshotKey());
    if (!raw) {
      // No snapshot -> ensure visible
      document.documentElement.classList.remove('__col_restore_hide');
      window.__col_restore_done = true;
      return;
    }

    let snap;
    try { snap = JSON.parse(raw); } catch {
      document.documentElement.classList.remove('__col_restore_hide');
      window.__col_restore_done = true;
      return;
    }

    if (!snap?.html) {
      document.documentElement.classList.remove('__col_restore_hide');
      window.__col_restore_done = true;
      return;
    }

    this._isRestoring = true;

    // Restore exact DOM state for this component
    this.innerHTML = snap.html;
    this._restoreApplied = true;

    const y = snap.scrollY || 0;

    // Disable smooth scrolling during restore
    const prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';

    // Two RAFs for reliable scroll restore
    requestAnimationFrame(() => {
      window.scrollTo(0, y);
      requestAnimationFrame(() => {
        window.scrollTo(0, y);

        document.documentElement.style.scrollBehavior = prev || '';

        // ✅ Mark restore complete + unhide now
        window.__col_restore_done = true;
        document.documentElement.classList.remove('__col_restore_hide');

        setTimeout(() => { this._isRestoring = false; }, 350);
      });
    });
  }

  _saveSnapshot() {
    const payload = {
      scrollY: window.scrollY,
      html: this.innerHTML,
      ts: Date.now()
    };

    try {
      sessionStorage.setItem(snapshotKey(), JSON.stringify(payload));
    } catch (e) {
      try {
        sessionStorage.removeItem(snapshotKey());
        sessionStorage.setItem(snapshotKey(), JSON.stringify(payload));
      } catch (_) {}
    }
  }

  _clearSnapshot() {
    try { sessionStorage.removeItem(snapshotKey()); } catch (_) {}
  }

  _onDocClickCapture = (e) => {
    const a = e.target?.closest?.('a');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (href.includes('/products/')) this._saveSnapshot();
  };

  _onScroll = () => {
    clearTimeout(this._scrollSaveT);
    this._scrollSaveT = setTimeout(() => this._saveSnapshot(), 250);
  };

  // -----------------------------
  // Original collection logic
  // -----------------------------

  _getForms() {
    this.$forms = new Map();
    this.$('collection-form').map((form) => this.$forms.set(form.id, form));
  }

  async _handleChange({ src, url, formId }) {
    // Prevent infinite scroll replay during restore
    if (this._isRestoring && src === 'dynamic-pagination') return;

    this._lastSrc = src;
    this.src = src;

    this._getUrlParams(url, formId);
    await this._getDoc();
    this._updateHistory();

    this.pub(Events.COLLECTION_UPDATED, { src, doc: this.$doc });

    if (src === 'dynamic-pagination') {
      this._saveSnapshot();
    } else {
      this._clearSnapshot();
    }
  }

  async _handleSortChange({ value }) {
    if (this._isRestoring) return;

    this._lastSrc = 'sort';
    this.sortBy = value;
    this.urlParams.set('sort_by', value);

    await this._getDoc();
    this._updateHistory();
    this.pub(Events.COLLECTION_UPDATED, { src: 'sort', doc: this.$doc });

    this._clearSnapshot();
  }

  set src(value) {
    const inert = (value === 'dynamic-pagination');
    this.withLoading = !inert;
    if (!inert) this._scrollToTop();
  }

  _initCache() {
    this.sortBy = this.prop('sort-by');
    const firstFormId = this.$forms.keys().next().value;
    this._getUrlParams(undefined, firstFormId);
    Cache.set(this.urlParams.toString(), document.cloneNode(true));
  }

  _getUrlParams(url, formId) {
    if (!url && !formId) {
      console.error('collection change does not contain resource');
      return;
    }

    const searchParams = url
      ? new URL(`${window.location.origin}${url}`).searchParams
      : new URLSearchParams(this._formData(formId));

    this.sortBy && searchParams.set('sort_by', this.sortBy);
    this.urlParams = searchParams;
  }

  _updateHistory() {
    this.urlParams.delete('section_id');

    // Never keep page= in URL for infinite scroll
    if (this._lastSrc === 'dynamic-pagination') {
      this.urlParams.delete('page');
      const qs = this.urlParams.toString();
      const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState({}, null, newUrl);
      return;
    }

    const qs = this.urlParams.toString();
    const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.pushState({}, null, newUrl);
  }

  _formData(formId) {
    if (!this.$forms.has(formId)) {
      console.error(`form not found with id ${formId}`);
      return;
    }
    return new FormData(this.$forms.get(formId));
  }

  async _getDoc() {
    const cacheId = this.urlParams.toString();
    if (Cache.has(cacheId)) {
      this.$doc = Cache.get(cacheId);
      return;
    }
    this.$doc = await this._fetchDoc();
    Cache.set(cacheId, this.$doc);
  }

  async _fetchDoc() {
    return await $fetch(this.prop('collection-url'), {
      before: this._loading(true),
      after: this._loading(false),
      sectionId: this.sectionId,
      params: this.urlParams
    });
  }

  _loading(state) {
    return () => {
      if (!this.withLoading && state) return;
      $loading(this.$('loader'), state);
    };
  }

  _scrollToTop() {
    window.scrollTo({
      top: this.topPoint + window.pageYOffset,
      behavior: 'smooth'
    });
  }

  get topPoint() {
    const headerHeight = document.documentElement.style
      .getPropertyValue('--header-height')
      ?.replace('px', '');
    const offset = this.getBoundingClientRect().top;
    return headerHeight ? offset - headerHeight : offset;
  }
});
